import { useMemo } from 'react'

import { selectAgentConversationFromId, selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { Show } from '@/assets/icons'
import { useArtifactReadOnlyExport } from '@/artifacts/hooks/useArtifactReadOnlyExport'
import { useArtifactViewableMarkdown } from '@/artifacts/hooks/useArtifactViewableMarkdown'
import { selectMatterMembers } from '@/collaboration/store/selectors'
import { getMemberDisplayName } from '@/collaboration/utils/members'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerHeader from '@/content-viewer/components/common/ContentViewerHeader'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

type ArtifactReadOnlyViewerProps = {
  conversationId: string
  sourceConversationId: string
  matterId: string | null
  artifactVersionId: string
  title: string
  highlightSentences: string[]
}

function getDraftOwnerText(ownerDisplayName: string | null): string {
  if (!ownerDisplayName) return "Viewing another collaborator's draft"
  const trimmedName = ownerDisplayName.trim()
  if (!trimmedName) return "Viewing another collaborator's draft"
  return `Viewing ${trimmedName}'s draft`
}

function ReadOnlyDraftBanner({ ownerDisplayName }: { ownerDisplayName: string | null }) {
  return (
    <div className="flex h-9 shrink-0 items-center border-b border-true-gray-200 bg-true-gray-100 px-2 py-1.5">
      <div className="flex min-w-0 items-center gap-1 rounded px-2 py-0.5">
        <Show className="h-3.5 w-3.5 shrink-0 text-brand-neutral-950" aria-hidden="true" />
        <p className="min-w-0 truncate text-xs font-medium leading-4 text-brand-neutral-950">{getDraftOwnerText(ownerDisplayName)}</p>
      </div>
    </div>
  )
}

export default function ArtifactReadOnlyViewer(props: ArtifactReadOnlyViewerProps) {
  const { conversationId, sourceConversationId, matterId, artifactVersionId, title, highlightSentences } = props
  const sourceConversation = useAppSelector((state: RootState) => selectAgentConversationFromId(state, { conversationId: sourceConversationId }))
  const sourceConversationMatterId = useAppSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId: sourceConversationId }))
  const conversationMembers = useAppSelector((state: RootState) => selectMatterMembers(state, sourceConversationMatterId ?? undefined))
  const draftOwnerDisplayName = useMemo(() => {
    if (!sourceConversation) return null
    const ownerMember = conversationMembers.find((member) => member.user_id === sourceConversation.user_id)
    return ownerMember ? getMemberDisplayName(ownerMember) : null
  }, [sourceConversation, conversationMembers])
  const { htmlString, isLoading, hasError, retry, referenceInfoByCitationId } = useArtifactViewableMarkdown({
    artifactVersionId,
    sourceConversationId,
    highlightSentences,
  })
  const { isExporting, onSave } = useArtifactReadOnlyExport({
    artifactVersionId,
    matterId,
    title,
    conversationId: sourceConversationId,
  })

  return (
    <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
      <ContentViewerHeader conversationId={conversationId} loading={false} title={title} isExporting={isExporting} onSave={onSave} />
      <ReadOnlyDraftBanner ownerDisplayName={draftOwnerDisplayName} />
      <div className="flex-1 w-full h-full overflow-y-auto pb-4">
        {isLoading ? <ContentViewerLoadingSpinner size="small" /> : null}
        {!isLoading && (hasError || !htmlString) ? <ContentViewerErrorState message="Failed to load markdown content." onRetry={retry} /> : null}
        {!isLoading && !hasError && htmlString ? (
          <HTMLBaseRenderer htmlContent={htmlString} highlightSentences={highlightSentences} referenceInfoByCitationId={referenceInfoByCitationId} />
        ) : null}
      </div>
    </div>
  )
}
